import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  ArrowRight,
  BarChart3,
  CheckCircle,
  Download,
  FileSpreadsheet,
  FileText,
  Globe2,
  Layers,
  Layers3,
  Loader2,
  Lock,
  Search,
  ShieldCheck,
  Sparkles,
  Table,
  Upload,
  Zap,
} from 'lucide-react'
import { authApi, documentsApi, type Document, type DocumentPreview, type ExportResponse } from '../api'
import { planDefinitions, type PlanDefinition } from '../plans'
import { useAuthStore } from '../store'
import { useToastStore } from '../store/toast'

const navItems = [
  ['Playground', '#playground'],
  ['How it works', '#how-it-works'],
  ['Features', '#features'],
  ['Use cases', '#use-cases'],
  ['Security', '#security'],
  ['Pricing', '#pricing'],
  ['FAQ', '#faq'],
]

const seoKeywords = {
  English: ['PDF table extraction', 'PDF to Excel', 'document parser', 'extract tables from PDF', 'CSV export', 'OCR-ready documents', 'spreadsheet automation'],
  Français: ['extraire tableau PDF', 'PDF vers Excel', 'convertir PDF en CSV', 'analyse de documents', 'extraction de données', 'automatisation bureautique'],
  العربية: ['استخراج الجداول من PDF', 'تحويل PDF إلى Excel', 'تحويل المستندات', 'استخراج البيانات', 'تصدير CSV', 'معالجة الوثائق'],
}

export default function LandingPage() {
  const { user, setUser } = useAuthStore()
  const addToast = useToastStore((state) => state.addToast)
  const [document, setDocument] = useState<Document | null>(null)
  const [preview, setPreview] = useState<DocumentPreview | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx')
  const [stage, setStage] = useState<'idle' | 'uploading' | 'processing' | 'ready' | 'exporting'>('idle')
  const [exportResult, setExportResult] = useState<ExportResponse['export'] | null>(null)
  const [playgroundOnly, setPlaygroundOnly] = useState(false)

  useEffect(() => {
    documentTitle()
    if (user) return
    authApi.me().then((res) => setUser(res.data)).catch(() => setUser(null))
  }, [user, setUser])

  const selectedCount = selectedIds.length
  const sampleTable = preview?.sample_table || preview?.tables[0]

  const refreshPreview = useCallback(async (doc: Document) => {
    const response = await documentsApi.preview(doc.id, 7)
    setPreview(response.data)
    setSelectedIds(response.data.columns.map((column) => column.id))
    setStage('ready')
  }, [])

  const waitForProcessed = useCallback(async (doc: Document) => {
    for (let attempt = 0; attempt < 180; attempt += 1) {
      const response = await documentsApi.get(doc.id)
      setDocument(response.data)
      if (['processed', 'export_ready'].includes(response.data.status)) {
        await refreshPreview(response.data)
        return
      }
      if (response.data.status === 'failed') throw new Error(response.data.error_message || 'Processing failed.')
      await new Promise((resolve) => setTimeout(resolve, 1500))
    }
    throw new Error('Processing is still running. Open the dashboard to continue.')
  }, [refreshPreview])

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (!file) return
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      addToast({ type: 'error', message: 'Only PDF files are supported right now.' })
      return
    }

    setPreview(null)
    setExportResult(null)
    setStage('uploading')
    try {
      if (!user) {
        setStage('processing')
        const response = await documentsApi.playgroundExtract(file, 7)
        setPreview(response.data)
        setDocument(response.data.document)
        setSelectedIds(response.data.columns.map((column) => column.id))
        setPlaygroundOnly(true)
        setStage('ready')
        addToast({ type: 'success', message: 'Preview is ready. Login or register to export.' })
        return
      }
      setPlaygroundOnly(false)
      const uploaded = await documentsApi.upload(file)
      setDocument(uploaded.data.document)
      setStage('processing')
      const started = await documentsApi.startProcessing(uploaded.data.document.id)
      setDocument(started.data.document)
      await waitForProcessed(started.data.document)
      addToast({ type: 'success', message: 'Preview is ready.' })
    } catch (err: any) {
      setStage('idle')
      addToast({ type: 'error', message: err.response?.data?.error || err.message || 'Could not process the PDF.' })
    }
  }, [user, addToast, waitForProcessed])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 25 * 1024 * 1024,
  })

  const toggle = (id: string) => {
    setSelectedIds((ids) => ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id])
  }

  const exportFile = async () => {
    if (!user || playgroundOnly) {
      window.location.href = authApi.googleStartUrl()
      return
    }
    if (!document || selectedIds.length === 0) return
    setStage('exporting')
    try {
      const response = await documentsApi.export(document.id, selectedIds, format)
      setExportResult(response.data.export)
      setDocument(response.data.document)
      addToast({ type: 'success', message: 'Export ready to download.' })
    } catch (err: any) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Export failed.' })
    } finally {
      setStage('ready')
    }
  }

  const stats = useMemo(() => preview ? [
    ['Pages', preview.page_count],
    ['Rows', preview.row_count],
    ['Columns', preview.column_count],
    ['Selected', `${selectedCount}/${preview.columns.length}`],
  ] : [], [preview, selectedCount])

  return (
    <main className="min-h-screen bg-white text-gray-950">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <a href="#" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-blue text-white grid place-items-center">
              <Layers3 className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold">ExcelOPDF</span>
          </a>
          <nav className="hidden lg:flex items-center gap-5 text-sm text-gray-600">
            {navItems.map(([label, href]) => <a key={href} href={href} className="hover:text-brand-blue">{label}</a>)}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <a href="/dashboard" className="px-4 py-2 rounded-lg bg-brand-blue text-white hover:bg-brand-accent">Open dashboard</a>
            ) : (
              <>
                <a href={authApi.googleStartUrl()} className="inline-flex px-3 sm:px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Login</a>
                <a href={authApi.googleStartUrl()} className="px-3 sm:px-4 py-2 rounded-lg bg-brand-blue text-white hover:bg-brand-accent">Register</a>
              </>
            )}
          </div>
        </div>
      </header>

      <section id="home" className="bg-[radial-gradient(circle_at_top_left,#eaf1ff,transparent_32rem)]">
        <div className="max-w-7xl mx-auto px-5 py-16 lg:py-20 grid lg:grid-cols-[0.95fr_1.05fr] gap-10 items-start">
          <div className="space-y-8">
            <div className="space-y-5">
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-blue bg-brand-light px-3 py-1.5 rounded-full">
                <Sparkles className="w-4 h-4" />
                AI-ready document table extraction
              </p>
              <h1 className="text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                Convert PDF tables into Excel and CSV without fighting layouts.
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl">
                Upload a document, preview extracted rows, select only the columns you need, then export clean spreadsheets for finance, operations, research, compliance, and data teams.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="#playground" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-brand-blue text-white font-semibold hover:bg-brand-accent">
                Try the playground
                <ArrowRight className="w-5 h-5" />
              </a>
              <a href="#pricing" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50">
                View pricing
              </a>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Trust label="Private workspace" icon={ShieldCheck} />
              <Trust label="Column selection" icon={Table} />
              <Trust label="Excel and CSV" icon={FileSpreadsheet} />
            </div>
          </div>

          <Playground
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            stage={stage}
            user={user}
            preview={preview}
            document={document}
            sampleTable={sampleTable}
            stats={stats}
            selectedIds={selectedIds}
            selectedCount={selectedCount}
            toggle={toggle}
            setSelectedIds={setSelectedIds}
            format={format}
            setFormat={setFormat}
            exportFile={exportFile}
            exportResult={exportResult}
            playgroundOnly={playgroundOnly}
          />
        </div>
      </section>

      <Section id="how-it-works" eyebrow="How it works" title="From messy PDF to spreadsheet in four clear steps.">
        <div className="grid md:grid-cols-4 gap-4">
          <Step n="01" title="Upload" text="Drop a PDF into the playground or your private dashboard." />
          <Step n="02" title="Extract" text="The engine detects tables, headers, repeated schemas, and wide data grids." />
          <Step n="03" title="Review" text="Preview rows, inspect columns, and remove fields you do not need." />
          <Step n="04" title="Export" text="Download CSV or formatted XLSX once you are signed in." />
        </div>
      </Section>

      <Section id="features" eyebrow="Features" title="Built for high-volume document workflows, not only one-off conversion.">
        <div className="grid md:grid-cols-3 gap-5">
          <Feature icon={Search} title="Structure-aware detection" text="Separates real data tables from headers, summaries, metadata blocks, and repeated footers." />
          <Feature icon={BarChart3} title="Extraction quality signals" text="See row counts, column counts, confidence, missing values, and warnings before export." />
          <Feature icon={Globe2} title="Multilingual documents" text="Works with left-to-right and right-to-left table content, including Arabic, French, and English documents." />
          <Feature icon={Zap} title="Background processing" text="Large PDFs keep processing while users navigate through the application." />
          <Feature icon={FileText} title="Duplicate prevention" text="Document hashing prevents users from uploading the same PDF repeatedly." />
          <Feature icon={Download} title="Selective export" text="Export all rows for the selected columns only, in CSV or Excel format." />
        </div>
      </Section>

      <Section id="advanced-features" eyebrow="Advanced Features" title="Powered by state-of-the-art OCR technology.">
        <div className="grid md:grid-cols-2 gap-5">
          <FeatureCard
            icon={Zap}
            title="Advanced OCR"
            description="State-of-the-art OCR model that handles complex tables, handwriting, and 90+ languages with superior accuracy."
            benefits={[
              'Complex layout reconstruction',
              'Handwriting recognition',
              '90+ language support',
              'Form and checkbox detection',
            ]}
          />
          <FeatureCard
            icon={Layers}
            title="Split-Screen Viewer"
            description="View your PDF and extracted data side-by-side with synchronized scrolling and table highlighting."
            benefits={[
              'Real-time PDF highlighting',
              'Synchronized scrolling',
              'Click-to-navigate between views',
              'Multi-table support',
            ]}
          />
        </div>
        <div className="mt-8 p-5 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-purple-900">Advanced Features Availability</h3>
              <p className="text-purple-700 mt-1">
                Advanced OCR is available for Pro users (50 scans/month) and Advanced users (unlimited).
                Split-Screen Viewer requires an Advanced subscription.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="use-cases" eyebrow="Use cases" title="A general document extraction platform for teams that live in PDFs.">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {['Invoices and statements', 'Public records and reports', 'Banking and finance tables', 'Research datasets', 'Administrative documents', 'Procurement files', 'Operations logs', 'Compliance archives'].map((item) => (
            <div key={item} className="border border-gray-200 rounded-lg p-4 bg-white">
              <p className="font-semibold">{item}</p>
              <p className="text-sm text-gray-600 mt-2">Extract structured rows, validate the preview, and export clean spreadsheet data.</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="pricing" eyebrow="Pricing" title="Start free, scale when documents become part of your workflow.">
        <div className="grid lg:grid-cols-3 gap-5">
          {planDefinitions.map((plan) => <PriceCard key={plan.name} plan={plan} />)}
        </div>
        <p className="mt-6 text-center text-sm text-gray-600">
          Same plans and limits apply in the dashboard. Monthly prices match the current Paddle catalog.
        </p>
      </Section>

      <Section id="seo-keywords" eyebrow="Multilingual search coverage" title="Find the right PDF table extraction tool in your language.">
        <div className="grid lg:grid-cols-3 gap-5">
          {Object.entries(seoKeywords).map(([language, keywords]) => (
            <div key={language} className="border border-gray-200 rounded-lg p-5 bg-white">
              <h3 className="font-semibold text-lg">{language}</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {keywords.map((keyword) => (
                  <span key={keyword} className="text-sm bg-gray-100 border border-gray-200 rounded-full px-3 py-1">{keyword}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section id="faq" eyebrow="FAQ" title="Questions people ask before choosing a PDF extraction tool.">
        <div className="grid md:grid-cols-2 gap-4">
          <FAQ q="Is this only a PDF converter?" a="No. The workflow focuses on structured table extraction, preview, column selection, and export quality." />
          <FAQ q="Can I use it before registering?" a="Yes. You can upload a PDF and preview seven sample rows. Exporting requires login." />
          <FAQ q="Does it support wide tables?" a="Yes. Preview grids scroll horizontally inside their panel, so page width stays stable." />
          <FAQ q="What formats are supported?" a="The current workflow supports PDF input and CSV or XLSX output." />
        </div>
      </Section>

      <Section id="security" eyebrow="Security" title="Enterprise-grade security, built from the ground up.">
        <div className="grid md:grid-cols-3 gap-5">
          <SecurityCard
            icon={Lock}
            title="Strong Encryption"
            description="All documents are encrypted at rest and in transit using industry-standard encryption protocols."
            status="active"
          />
          <SecurityCard
            icon={ShieldCheck}
            title="Digital Signatures"
            description="Cryptographic signatures ensure document integrity and authenticity throughout processing."
            status="active"
          />
          <SecurityCard
            icon={Layers3}
            title="Secure Storage"
            description="Isolated storage with access controls and audit logging for complete data governance."
            status="active"
          />
        </div>
        <div className="mt-8 p-5 bg-brand-light border border-brand-blue/20 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-brand-blue" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Security Roadmap</h3>
              <p className="text-gray-700 mt-1">
                We are committed to achieving the highest security standards. Within the next year, the entire service will be upgraded to use strong encryption end-to-end, including encrypted processing pipelines and zero-knowledge architecture options.
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm text-brand-blue">
                <span className="font-semibold">Timeline: 2026–2027</span>
                <span className="text-gray-500">•</span>
                <span>Full encryption rollout in progress</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-5 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-brand-blue text-white grid place-items-center">
                  <Layers3 className="w-5 h-5" />
                </div>
                <span className="text-xl font-bold text-white">ExcelOPDF</span>
              </div>
              <p className="text-sm text-gray-400">
                Extract tables from PDF documents with precision. Built for finance, research, and enterprise workflows.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#playground" className="hover:text-white">Playground</a></li>
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="#security" className="hover:text-white">Security</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#how-it-works" className="hover:text-white">How it works</a></li>
                <li><a href="#use-cases" className="hover:text-white">Use cases</a></li>
                <li><a href="#faq" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/terms-of-service" className="hover:text-white">Terms of Service</a></li>
                <li><a href="/privacy-policy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/refund-policy" className="hover:text-white">Refund Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">© 2026 ExcelOPDF. All rights reserved.</p>
            <p className="text-sm text-gray-500">Work in progress — Security enhancements rolling out through 2027.</p>
          </div>
        </div>
      </footer>
    </main>
  )
}

function Playground(props: any) {
  const {
    getRootProps, getInputProps, isDragActive, stage, user, preview, document, sampleTable, stats,
    selectedIds, selectedCount, toggle, setSelectedIds, format, setFormat, exportFile, exportResult, playgroundOnly,
  } = props

  return (
    <section id="playground" className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden min-w-0">
      <div className="p-5 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-lg">Document Playground</h2>
          <p className="text-sm text-gray-600">Upload, preview 7 rows, select columns, export after login.</p>
        </div>
        <span className="text-xs font-semibold bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1">PDF to table</span>
      </div>
      <div className="p-5 space-y-5 min-w-0">
        <div
          {...getRootProps()}
          className={`border border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive ? 'border-brand-blue bg-brand-light' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
            } ${['uploading', 'processing', 'exporting'].includes(stage) ? 'opacity-60 pointer-events-none' : ''}`}
        >
          <input {...getInputProps()} />
          {['uploading', 'processing'].includes(stage) ? <Loader2 className="w-10 h-10 text-brand-blue mx-auto mb-4 animate-spin" /> : <Upload className="w-10 h-10 text-brand-blue mx-auto mb-4" />}
          <p className="font-semibold">{stage === 'uploading' ? 'Uploading...' : stage === 'processing' ? 'Processing...' : 'Drag and drop your PDF here'}</p>
          <p className="text-sm text-gray-500 mt-1">or click to browse. PDF only, max 25MB.</p>
        </div>

        {!user && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <Lock className="w-4 h-4" />
            Preview is free. Login or register to export and download.
          </div>
        )}

        {preview && (
          <div className="border border-gray-200 rounded-lg overflow-hidden max-w-full min-w-0">
            <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold truncate">Preview: {document?.original_filename}</h3>
                  <p className="text-sm text-gray-600">{profileLabel(preview.profile)}. Showing 7 sample rows.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                {stats.map(([label, value]: any[]) => <MiniStat key={label} label={label} value={value} />)}
              </div>
            </div>

            <div className="p-4 border-b border-gray-200 max-w-full overflow-hidden">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm font-medium">{selectedCount} columns selected</p>
                <button onClick={() => setSelectedIds(selectedCount === preview.columns.length ? [] : preview.columns.map((column: any) => column.id))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50">
                  {selectedCount === preview.columns.length ? 'Unselect all' : 'Select all'}
                </button>
              </div>
              <div className="overflow-x-auto max-w-full">
                <div className="grid gap-2 min-w-max" style={{ gridTemplateColumns: `repeat(${preview.columns.length}, minmax(8rem, 1fr))` }}>
                  {preview.columns.map((column: any) => (
                    <button key={column.id} onClick={() => toggle(column.id)} className={`rounded border px-3 py-2 text-sm text-right arabic-text ${selectedIds.includes(column.id) ? 'border-brand-blue bg-brand-light text-brand-blue' : 'border-gray-200 text-gray-700'}`}>
                      {column.column_name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto max-w-full">
              <table className="text-sm min-w-max">
                <thead className="bg-gray-50">
                  <tr>{(sampleTable?.columns || []).map((column: string, index: number) => <th key={`${column}-${index}`} className="px-3 py-3 text-right text-gray-700 arabic-text whitespace-nowrap">{column}</th>)}</tr>
                </thead>
                <tbody>
                  {(sampleTable?.rows || []).slice(0, 7).map((row: any[], rowIndex: number) => (
                    <tr key={rowIndex} className="border-t border-gray-100">
                      {(sampleTable?.columns || []).map((column: string, columnIndex: number) => <td key={`${column}-${columnIndex}`} className="px-3 py-2 text-right text-gray-950 arabic-text whitespace-nowrap">{row[columnIndex] || ''}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <select value={format} onChange={(event) => setFormat(event.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-950">
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
              </select>
              <div className="flex items-center gap-3">
                <button onClick={exportFile} disabled={selectedIds.length === 0 || stage === 'exporting'} className="px-4 py-2 rounded-lg bg-brand-blue text-white hover:bg-brand-accent disabled:opacity-50">
                  {stage === 'exporting' ? 'Exporting...' : user && !playgroundOnly ? 'Export selected' : 'Login to export'}
                </button>
                {exportResult && <a href={exportResult.download_url} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"><Download className="w-4 h-4" />Download</a>}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function Section({ id, eyebrow, title, children }: { id: string; eyebrow: string; title: string; children: any }) {
  return (
    <section id={id} className="max-w-7xl mx-auto px-5 py-16 border-t border-gray-100">
      <p className="text-sm font-semibold text-brand-blue">{eyebrow}</p>
      <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-2 mb-8 max-w-3xl">{title}</h2>
      {children}
    </section>
  )
}

function Trust({ label, icon: Icon }: { label: string; icon: any }) {
  return <div className="flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2"><Icon className="w-4 h-4 text-brand-blue" />{label}</div>
}

function Step({ n, title, text }: { n: string; title: string; text: string }) {
  return <div className="border border-gray-200 rounded-lg p-5 bg-white"><p className="text-brand-blue font-bold">{n}</p><h3 className="font-semibold text-lg mt-3">{title}</h3><p className="text-sm text-gray-600 mt-2">{text}</p></div>
}

function Feature({ icon: Icon, title, text }: { icon: any; title: string; text: string }) {
  return <div className="border border-gray-200 rounded-lg p-5 bg-white"><Icon className="w-6 h-6 text-brand-blue" /><h3 className="font-semibold text-lg mt-4">{title}</h3><p className="text-sm text-gray-600 mt-2">{text}</p></div>
}

function FeatureCard({ icon: Icon, title, description, benefits }: { icon: any; title: string; description: string; benefits: string[] }) {
  return (
    <div className="border border-purple-200 rounded-xl p-6 bg-white shadow-sm">
      <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-purple-600" />
      </div>
      <h3 className="font-semibold text-xl text-gray-900">{title}</h3>
      <p className="text-gray-600 mt-2">{description}</p>
      <ul className="mt-4 space-y-2">
        {benefits.map((benefit, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
            <span className="text-gray-700">{benefit}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function PriceCard({ plan }: { plan: PlanDefinition }) {
  return (
    <div className={`relative flex h-full flex-col rounded-2xl border-2 p-6 transition-all ${plan.highlight ? 'border-brand-blue shadow-lg bg-brand-light/30' : 'border-gray-200 bg-white'
      }`}>
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-brand-blue text-white text-xs font-semibold px-3 py-1 rounded-full">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
        <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
        <div className="mt-4">
          {plan.price.monthly > 0 ? (
            <>
              <span className="text-4xl font-bold text-gray-900">{plan.currency}{plan.price.monthly}</span>
              <span className="text-gray-500">/mo</span>
              <p className="text-xs text-gray-500 mt-1">Monthly billing</p>
            </>
          ) : (
            <span className="text-4xl font-bold text-gray-900">Free</span>
          )}
        </div>
      </div>

      <ul className="mb-6 flex-1 space-y-3">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2">
            {feature.included ? (
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            ) : (
              <Lock className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
            )}
            <span className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>{feature.text}</span>
          </li>
        ))}
      </ul>

      <a
        href={authApi.googleStartUrl()}
        className={`w-full py-3 px-4 rounded-xl font-semibold transition-colors text-center ${plan.highlight
          ? 'bg-brand-blue text-white hover:bg-brand-accent'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
      >
        {plan.cta}
      </a>
    </div>
  )
}

function FAQ({ q, a }: { q: string; a: string }) {
  return <div className="border border-gray-200 rounded-lg p-5 bg-white"><h3 className="font-semibold">{q}</h3><p className="text-sm text-gray-600 mt-2">{a}</p></div>
}

function MiniStat({ label, value }: { label: any; value: any }) {
  return <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 min-w-20"><p className="text-xs text-gray-500">{label}</p><p className="font-semibold">{value}</p></div>
}

function profileLabel(profile: string) {
  if (profile.includes('wide')) return 'Wide recurring table'
  if (profile.includes('financial')) return 'Structured financial table'
  return 'Generic document table'
}

function documentTitle() {
  document.title = 'ExcelOPDF | PDF table extraction, PDF to Excel, CSV export'
  const description = 'Extract tables from PDF documents, preview rows, select columns, and export clean Excel or CSV files. Multilingual document data extraction in English, French, and Arabic.'
  const keywords = [...seoKeywords.English, ...seoKeywords.Français, ...seoKeywords.العربية].join(', ')
  setMeta('description', description)
  setMeta('keywords', keywords)
}

function setMeta(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    tag.name = name
    document.head.appendChild(tag)
  }
  tag.content = content
}

function SecurityCard({ icon: Icon, title, description, status }: { icon: any; title: string; description: string; status: string }) {
  return (
    <div className="border border-gray-200 rounded-lg p-5 bg-white">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center">
          <Icon className="w-5 h-5 text-brand-blue" />
        </div>
        {status === 'active' && (
          <span className="text-xs font-semibold bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">Active</span>
        )}
      </div>
      <h3 className="font-semibold text-lg mt-4">{title}</h3>
      <p className="text-sm text-gray-600 mt-2">{description}</p>
    </div>
  )
}
